import React from 'react';
import Field from '../../Field';
import ReminderLeadField from './ReminderLeadField';
import { CHANNELS } from '../../../domain/notifications';
import { SWATCHES, categoryIsBillable } from '../../../domain/categories';
import s from './categories.module.css';
import SmsUnavailable from '../../SmsUnavailable';
import { useSmsAvailable } from '../../../context/SmsGateContext';

// The palette is domain/categories.js's, shared with the tag editor (#138),
// and re-exported here for CategoriesDrawer and the tests.
export { SWATCHES };

// Everything a category is, as one set of controls, for both surfaces: MMS's
// Edit Category drawer and the Add block at the foot of the list (#98).
//
// MMS's Edit Category carries Name, Color, Email Reminders, SMS Reminders,
// Make-Up Credits and Blocked Dates:
//
//   * Make-Up Credits is the checkbox below, ported verbatim (#351). It decides
//     whether the make-up machinery applies to an event type at all, which is a
//     different question from a student's credit balance (`domain/makeup.js`);
//     the gate is `statuses_for` in `core/src/attendance.rs`.
//   * Blocked Dates has no equivalent: nothing would read one (#73).
//
// Billable is ours, not MMS's: it stops a Recital or an Unavailable block
// generating a charge whatever the student's rate says.

export default function CategoryFields({
  draft, onChange, idPrefix, disabled = false, errors = {},
}) {
  const set = (patch) => onChange({ ...draft, ...patch });
  const leads = draft.reminderLeadHours || {};
  const smsAvailable = useSmsAvailable();

  return (
    <>
      {/* `errors` is what the last Add or Save refused, keyed by field —
          `categoryErrors` in domain/categories.js, computed in
          CategoriesDrawer. Empty until the first submit: the refusal arrives
          on submit, not while the teacher is typing (#116, #129). */}
      <Field label="Name" htmlFor={`${idPrefix}-name`} error={errors.label}>
        <input
          id={`${idPrefix}-name`}
          className={s.input}
          value={draft.label}
          disabled={disabled}
          data-error={errors.label ? 'true' : undefined}
          onChange={(e) => set({ label: e.target.value })}
        />
      </Field>

      <div className={s.swatches}>
        {SWATCHES.map((sw) => (
          <button
            key={sw}
            type="button"
            aria-label={`Colour ${sw}`}
            aria-pressed={draft.color === sw}
            className={draft.color === sw ? s.swatchButtonOn : s.swatchButton}
            style={{ background: sw }}
            disabled={disabled}
            onClick={() => set({ color: sw })}
          />
        ))}
      </div>

      <label className={s.checkRow}>
        <input
          type="checkbox"
          checked={categoryIsBillable(draft)}
          disabled={disabled}
          onChange={(e) => set({ billable: e.target.checked })}
        />
        <span>Events in this category can generate charges</span>
      </label>

      {/* MMS's own label, verbatim, under its own Make-Up Credits heading
          (#351). Despite "to be used" it gates issuing a credit as well as
          spending one, so the hint under it says so.

          `=== true`, not the `!== false` Billable uses, because the two
          default opposite ways: a category silent about charges is billable,
          one silent about make-up credits is off. Both drafts set the key to a
          boolean (`emptyDraft` and `draftOf` in CategoriesDrawer.jsx); the
          guard keeps `checked` from ever being `undefined`, which would make
          React drop to an uncontrolled input that ignores the draft. */}
      <label className={s.checkRow}>
        <input
          type="checkbox"
          checked={draft.allowMakeupCredits === true}
          disabled={disabled}
          onChange={(e) => set({ allowMakeupCredits: e.target.checked })}
        />
        <span>Allow make-up credits to be used for this event type</span>
      </label>
      <div className={s.checkHint}>
        This decides both directions: with it off, a lesson in this category
        cannot issue a make-up credit or spend one.
      </div>

      {/* Both dropdowns on a new category start at No Reminders, as MMS ships
          every stock category. */}
      <div className={s.leadRow}>
        <ReminderLeadField
          id={`${idPrefix}-email-lead`}
          label="Email Reminders"
          disabled={disabled}
          error={errors.emailLead}
          value={leads[CHANNELS.email] ?? null}
          onChange={(hours) => set({ reminderLeadHours: { ...leads, [CHANNELS.email]: hours } })}
        />
        {/* Disabled and drawn as No Reminders while texts cannot be sent
            (#782, domain/smsGate.js). The draft keeps the stored lead, so
            Save writes it back unchanged; meanwhile the reminder worker
            queues a text the transport refuses. */}
        <ReminderLeadField
          id={`${idPrefix}-sms-lead`}
          label="SMS Reminders"
          disabled={disabled || !smsAvailable}
          error={errors.smsLead}
          value={smsAvailable ? (leads[CHANNELS.sms] ?? null) : null}
          onChange={(hours) => {
            if (smsAvailable) set({ reminderLeadHours: { ...leads, [CHANNELS.sms]: hours } });
          }}
          note={smsAvailable ? null : <SmsUnavailable />}
        />
      </div>
    </>
  );
}
